import React, { useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowRight
} from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  dark?: boolean;
  /**
   * What is being counted. Defaults to "eventos", which is what this bar said for every caller
   * before the prop existed — including the ones listing people.
   */
  itemNoun?: string;
  /**
   * Render the bar even when everything fits on one page.
   *
   * Off by default: navigation with nowhere to go is noise. On for lists whose TOTAL is itself
   * the answer the reader came for, which disappearing on the last page would hide.
   */
  alwaysShow?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  isLoading = false,
  dark = false,
  itemNoun = 'eventos',
  alwaysShow = false,
}) => {
  const [goToPage, setGoToPage] = useState('');

  // Calculate display range
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Generate page numbers with ellipses
  const getPageNumbers = (): (number | string)[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    // Always show first page
    pages.push(1);

    if (currentPage > 3) {
      pages.push('...');
    }

    // Show pages around current
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    if (currentPage < totalPages - 2) {
      pages.push('...');
    }

    // Always show last page
    if (totalPages > 1 && !pages.includes(totalPages)) {
      pages.push(totalPages);
    }

    return pages;
  };

  const handleGoToPage = () => {
    const pageNum = parseInt(goToPage, 10);
    if (pageNum >= 1 && pageNum <= totalPages && pageNum !== currentPage) {
      onPageChange(pageNum);
      setGoToPage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  if (totalPages <= 1 && !alwaysShow) {
    return null;
  }
  const navigable = totalPages > 1;

  const pageNumbers = getPageNumbers();

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t ${
      dark
        ? 'bg-gray-900 border-gray-800'
        : 'bg-white border-gray-200'
    }`}>
      {/* Info */}
      <div className={`text-sm order-2 sm:order-1 ${dark ? 'text-gray-500' : 'text-gray-500'}`}>
        Mostrando{' '}
        <span className={`font-medium ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{startItem}</span>
        {' '}-{' '}
        <span className={`font-medium ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{endItem}</span>
        {' '}de{' '}
        <span className={`font-medium ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{totalItems}</span>
        {' '}{itemNoun}
      </div>

      {/* Navigation. Dropped entirely on a single page, where `alwaysShow` keeps only the count. */}
      <div className={`flex items-center gap-1 order-1 sm:order-2 ${navigable ? '' : 'hidden'}`}>
        {/* First Page */}
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1 || isLoading}
          className={`p-1.5 rounded-md border disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${
            dark
              ? 'border-gray-700 hover:bg-gray-800'
              : 'border-gray-300 hover:bg-gray-50'
          }`}
          title="Primeira página"
        >
          <ChevronsLeft className={`h-4 w-4 ${dark ? 'text-gray-400' : 'text-gray-600'}`} />
        </button>

        {/* Previous Page */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
          className={`p-1.5 rounded-md border disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${
            dark
              ? 'border-gray-700 hover:bg-gray-800'
              : 'border-gray-300 hover:bg-gray-50'
          }`}
          title="Página anterior"
        >
          <ChevronLeft className={`h-4 w-4 ${dark ? 'text-gray-400' : 'text-gray-600'}`} />
        </button>

        {/* Page Numbers */}
        <div className="flex items-center gap-1 mx-1">
          {pageNumbers.map((page, index) => (
            typeof page === 'number' ? (
              <button
                key={index}
                onClick={() => onPageChange(page)}
                disabled={isLoading}
                className={`min-w-[32px] h-8 px-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${
                  page === currentPage
                    ? 'bg-cyan-600 text-white border border-cyan-600'
                    : dark
                      ? 'text-gray-300 border border-gray-700 hover:bg-gray-800'
                      : 'text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ) : (
              <span key={index} className={`px-1 select-none ${dark ? 'text-gray-600' : 'text-gray-400'}`}>
                ...
              </span>
            )
          ))}
        </div>

        {/* Next Page */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || isLoading}
          className={`p-1.5 rounded-md border disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${
            dark
              ? 'border-gray-700 hover:bg-gray-800'
              : 'border-gray-300 hover:bg-gray-50'
          }`}
          title="Próxima página"
        >
          <ChevronRight className={`h-4 w-4 ${dark ? 'text-gray-400' : 'text-gray-600'}`} />
        </button>

        {/* Last Page */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages || isLoading}
          className={`p-1.5 rounded-md border disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${
            dark
              ? 'border-gray-700 hover:bg-gray-800'
              : 'border-gray-300 hover:bg-gray-50'
          }`}
          title="Última página"
        >
          <ChevronsRight className={`h-4 w-4 ${dark ? 'text-gray-400' : 'text-gray-600'}`} />
        </button>

        {/* Go to Page */}
        <div className={`flex items-center gap-1 ml-2 pl-2 border-l ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
          <span className={`text-xs hidden sm:inline ${dark ? 'text-gray-500' : 'text-gray-500'}`}>Ir para:</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={goToPage}
            onChange={(e) => setGoToPage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="#"
            className={`w-12 h-8 px-2 text-sm text-center rounded-md focus:outline-none focus:ring-1 ${
              dark
                ? 'bg-gray-800 border border-gray-700 text-gray-200 placeholder-gray-600 focus:ring-cyan-500 focus:border-cyan-500'
                : 'border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
            }`}
            disabled={isLoading}
          />
          <button
            onClick={handleGoToPage}
            disabled={!goToPage || isLoading}
            className={`p-1.5 rounded-md border disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${
              dark
                ? 'border-gray-700 hover:bg-gray-800'
                : 'border-gray-300 hover:bg-gray-50'
            }`}
            title="Ir para página"
          >
            <ArrowRight className={`h-4 w-4 ${dark ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>
      </div>

      {/* Page indicator */}
      <div className={`text-sm order-3 ${navigable ? 'hidden lg:block' : 'hidden'} ${dark ? 'text-gray-500' : 'text-gray-500'}`}>
        Página{' '}
        <span className={`font-medium ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{currentPage}</span>
        {' '}de{' '}
        <span className={`font-medium ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{totalPages}</span>
      </div>
    </div>
  );
};

export default Pagination;
